// ============================================
// SmartInsightsCard Component
// Blue variant of AI insights card for Transactions page
// ============================================

import { SparklesIcon } from '../../../components/icons';
import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

interface SmartInsightsCardProps {
  /** Title text */
  title: string;
  /** Highlighted text (e.g., store name) */
  highlight?: string;
  /** Metric/insight detail */
  metric?: string;
  /** Description/recommendation text */
  description: string;
  /** CTA button label */
  ctaLabel: string;
  /** CTA click handler */
  onCtaClick: () => void;
  /** Loading state */
  isLoading?: boolean;
  /** Additional className */
  className?: string;
}

// ============================================
// Component
// ============================================

export function SmartInsightsCard({
  title,
  highlight,
  metric,
  description,
  ctaLabel,
  onCtaClick,
  isLoading,
  className,
}: SmartInsightsCardProps) {
  if (isLoading) {
    return (
      <div
        className={cn(
          'flex h-full min-h-[280px] animate-pulse flex-col rounded-xl bg-[#004AC6] p-6',
          className
        )}
      >
        <div className="mb-4 h-10 w-10 rounded-lg bg-white/10" />
        <div className="mb-2 h-6 w-3/4 rounded bg-white/10" />
        <div className="mb-1 h-4 w-full rounded bg-white/10" />
        <div className="h-4 w-2/3 rounded bg-white/10" />
        <div className="mt-auto h-10 w-32 rounded-lg bg-white/10" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex h-full flex-col rounded-xl bg-[#004AC6] p-6',
        className
      )}
      style={{ boxShadow: '0px 4px 20px rgba(0, 74, 198, 0.3)' }}
    >
      {/* AI Icon */}
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-white/10">
        <SparklesIcon size={20} className="text-blue-200" />
      </div>

      {/* Title with highlight */}
      <h3 className="mb-2 text-lg font-bold text-white">
        {highlight && (
          <>
            <span className="text-blue-200">{highlight}</span>{' '}
          </>
        )}
        {title}
        {metric && (
          <span className="text-blue-200"> {metric}</span>
        )}
      </h3>

      {/* Description */}
      <p className="mb-6 flex-1 text-sm leading-relaxed text-blue-100">
        {description}
      </p>

      {/* CTA Link */}
      <button
        onClick={onCtaClick}
        className="inline-flex w-fit items-center gap-2 text-sm font-semibold text-white underline underline-offset-2 transition-colors hover:text-blue-200"
      >
        {ctaLabel}
        <span aria-hidden="true">→</span>
      </button>
    </div>
  );
}
